import * as crud from "enums/crud";
import * as opt from "lib/opt";
import * as hooks from "lib/hooks";
import { Menu as MenuIcon } from "@mui/icons-material";
import * as routes from "enums/routes";
import * as sort from "enums/sort";
import { BaseTableProps } from "lib/table";
import { ContainmentStatusEnum } from "enums/containmentStatus";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { FiltersCtx } from "pages";
import * as table from "lib/table";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
	alpha,
	Box,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
	Button,
} from "@mui/material";
import {
	BaseTable,
	ContainmentDetails,
	EditContainment,
	EditContainmentRef,
	EditViewContainmentSidebar,
	MCCModal,
} from "components";
import { FilterList as FilterListIcon } from "@mui/icons-material";
import { Containment } from "models";
import { ContainmentsSlice, RootState, SelectContainmentById } from "store";
import { useSelector } from "react-redux";
import { Crud } from "enums/crud";
import { ContainmentFilterKey } from "enums/filters";
import { GradientHeaderText } from "lib/styles/header";
import { ContextMenuStyle } from "lib/styles/contextMenu";
import { LoadingButtonStyle } from "lib/styles/button";

import { TableHeaderStyles } from "lib/styles/table";
import {
	OBJECT_ID_ASRS_LOCK,
	OBJECT_ID_ASRS_UNLOCK,
	OBJECT_ID_CREATE_CONTAINMENT,
	OBJECT_ID_HOLD_CONTAINMENT,
} from "pages/Users/constants";

export function DashboardTable({
	containments,
	page,
	rowsPerPage,
	loading = false,
	title,
	tableProps,
	onToggleFilters,
	filtersActive,
	filtersOpen,
}: {
	containments: Containment[];
	page: int;
	rowsPerPage: int;
	loading?: bool;
	title?: string;
	tableProps?: Partial<
		BaseTableProps<
			Containment,
			keyof Containment,
			{ statuses: ContainmentStatusEnum }
		>
	>;
	onToggleFilters: () => void;
	filtersActive: boolean;
	filtersOpen: boolean;
}) {
	const { theme } = hooks.UseTheme();
	const filtersCtx = useContext(FiltersCtx);
	const permissions = hooks.UseHasPermission();
	const { reset } = hooks.UseFormContext();

	const selectedContainment = useSelector(
		ContainmentsSlice.selectors.selectSelectedContainment
	);
	const onContainmentSelect = hooks.UseAction(
		ContainmentsSlice.actions.SetSelectedContainment
	);

	const containmentHeadCells = useMemo(
		() =>
			table.CreateHeadCells<
				Containment,
				Partial<{ statuses: ContainmentStatusEnum }>,
				keyof Containment,
				ContainmentFilterKey
			>(
				[
					{ id: "friendlyId", numeric: false, label: "MCC ID" },
					{ id: "name", numeric: false, label: "Containment Name" },
					{
						id: "Status",
						numeric: false,
						label: "Upload Status",
						mapper: (o) => o.Name,
						sortKey: "status.name",
					},
					{
						id: "SourceSystem",
						numeric: false,
						label: "Containment Type",
						sortKey: "sourceSystem",
					},
					{
						id: "Plants",
						numeric: false,
						label: "Impacted Plants",
						sortable: false,
						mapper: (plants) => {
							return (plants || [])
								.slice()
								.sort((a, b) =>
									(a?.name || "").localeCompare(b?.name || "")
								)
								.map((p) => p?.name)
								.filter(Boolean)
								.join(", ");
						},
					},
					{
						id: "totalSerialCount",
						numeric: false,
						sortable: false,
						label: "Total Serial/LPN Count",
						mapper: (n) => String(n ?? 0),
						sortKey: "totalSerialCount",
					},
					{
						id: "createUser",
						numeric: false,
						label: "Created By",
						mapper: (o) => o?.name || "User Not Found",
						sortKey: "createUser.name",
					},
					{
						id: "createDate",
						numeric: false,
						label: "Created Date",
						mapper: (o) =>
							moment.utc(o).local().format("DD-MMM-YY HH:mm:ss"),
					},
				],
				(property) => {
					const isAsc =
						filtersCtx.filters.sort.Is("Asc") &&
						filtersCtx.filters.sort.Data === property;
					const newDir = isAsc ? sort.Desc : sort.Asc;
					filtersCtx.setSortParam(newDir(property));
				}
			),
		[filtersCtx.filters.sort, filtersCtx.setSortParam]
	);

	const [menuProps, setMenuProps] = hooks.UseOption<{
		el: HTMLElement;
		containment: Containment;
	}>(opt.None());

	const handleClose = () => setMenuProps(opt.None());

	const navigate = useNavigate();

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const editContainmentRef = useRef<EditContainmentRef>(null);

	const [mode, setMode] = hooks.UseReduxSetState(
		ContainmentsSlice.selectors.selectMode,
		ContainmentsSlice.actions.SetMode
	);

	// Function to close sidebar and reset all related state
	const closeSidebarAndResetState = useCallback(() => {
		editContainmentRef.current?.resetForm();
		onContainmentSelect(opt.None()); // Clear selected containment
		setMode(crud.Read());
	}, [onContainmentSelect, setMode]);

	// Handle sidebar close request
	const handleSidebarClose = useCallback(() => {
		if (editContainmentRef.current?.hasChanges()) {
			// If editing, show confirmation dialog instead of closing
			setShowConfirmDialog(true);
		} else {
			// If not editing, close sidebar immediately
			closeSidebarAndResetState();
		}
	}, [closeSidebarAndResetState, setShowConfirmDialog]);

	// when filters change close out the sidebar
	useEffect(() => {
		handleSidebarClose();
	}, [filtersCtx.filters, handleSidebarClose]);

	// Handler for confirming discard in the dialog
	const handleConfirmDiscard = () => {
		setShowConfirmDialog(false);

		pendingRowSelection.IfSome((selection) => {
			// If action is 'open', open the new sidebar
			if (selection.action === "open") {
				openSidebar(selection.containment, selection.mode);
			} else {
				closeSidebarAndResetState();
			}

			// Reset pending selection
			setPendingRowSelection(opt.None());
		});

		// If no pending selection for some reason, just close sidebar
		if (pendingRowSelection.IsNone()) {
			closeSidebarAndResetState();
		}
	};

	const handleNav = (containment: Containment) => {
		reset();
		const route = routes.Upload;
		navigate(route.PathWithParams(containment.id));
	};

	const dashboardActions: React.ReactNode[] = [
		<Tooltip key={"filters"} title="Filters">
			<IconButton
				data-test-selector="containment-filters-button"
				onClick={onToggleFilters}
			>
				<FilterListIcon
					fontSize="small"
					color={filtersOpen || filtersActive ? "primary" : "action"}
				/>
			</IconButton>
		</Tooltip>,
	];

	const containmentActions: React.ReactNode[] = [
		<MenuItem key="view" onClick={() => onMenuItemClick(crud.Read())}>
			View Details
		</MenuItem>,
	];

	// Check for Admin role for Upload, Edit Containment, and Add Serials actions
	// L1 (ASRS Lock) users can also access Add Containment; page-level permissions
	// for what they can do once inside will be handled in future work.
	if (
		permissions.createContainmentPlantIds.length > 0 ||
		permissions.asrsLockPlantIds.length > 0
	) {
		dashboardActions.unshift(
			<Button
				data-test-selector="containment-upload-button"
				variant="contained"
				onClick={() => {
					reset();
					navigate(routes.Upload.Path);
				}}
				sx={[LoadingButtonStyle(theme), { flexShrink: 0 }]}
			>
				+ Add Containment
			</Button>
		);
	}

	menuProps.IfSome((p) => {
		const canEditContainment = p.containment.TypeConfig?.usesLockPermissions
			? permissions.hasPermission(p.containment, OBJECT_ID_ASRS_LOCK) ||
				permissions.hasPermission(p.containment, OBJECT_ID_ASRS_UNLOCK)
			: permissions.hasPermission(
					p.containment,
					OBJECT_ID_CREATE_CONTAINMENT
				) ||
				permissions.hasPermission(
					p.containment,
					OBJECT_ID_HOLD_CONTAINMENT
				);
		if (canEditContainment) {
			containmentActions.Append(
				<MenuItem
					data-test-selector="edit-containment-button"
					onClick={() => onMenuItemClick(crud.Update())}
				>
					Edit Containment
				</MenuItem>
			);
		}
	});

	const closeSidebar = hooks.UseAction(
		ContainmentsSlice.actions.CloseSidebar
	);

	const [pendingRowSelection, setPendingRowSelection] = hooks.UseOption<{
		containment: Containment;
		action: "open" | "close";
		mode: Crud;
	}>();

	useEffect(() => {
		// reset the mode and containment
		return closeSidebar;
	}, [closeSidebar]);

	const effectiveModeForContainment = useCallback(
		(c: Containment, m: Crud): Crud => {
			if (!m.Is("Update")) return m;
			const canEdit = c.TypeConfig?.usesLockPermissions
				? permissions.hasPermission(c, OBJECT_ID_ASRS_LOCK) ||
					permissions.hasPermission(c, OBJECT_ID_ASRS_UNLOCK)
				: permissions.hasPermission(c, OBJECT_ID_CREATE_CONTAINMENT) ||
					permissions.hasPermission(c, OBJECT_ID_HOLD_CONTAINMENT);
			return canEdit ? m : crud.Read();
		},
		[permissions]
	);

	const openSidebar = (c: Containment, m: Crud) => {
		const effectiveMode = effectiveModeForContainment(c, m);
		setMode(effectiveMode);
		onContainmentSelect(opt.Some(c)); // Set selected containment
	};

	const onMenuItemClick = (m: Crud) => {
		setMenuProps(opt.None()); // Close menu first

		menuProps.IfSome((p) => {
			if (editContainmentRef.current?.hasChanges()) {
				// Store the information about which action to take after confirmation
				const shouldClose = selectedContainment
					.IfSome((c) => c.id.Equal(p.containment.id))
					.UnwrapOr(false);

				setPendingRowSelection(
					opt.Some({
						containment: p.containment,
						action: shouldClose ? "close" : "open",
						mode: m,
					})
				);

				// Show confirmation dialog
				setShowConfirmDialog(true);
			} else {
				// No unsaved changes, proceed normally
				if (selectedContainment.IsSome()) {
					// If already a row selected
					if (
						!selectedContainment
							.Expect("Containment not found")
							.id.Equal(p.containment.id)
					) {
						// Different row - select new row
						onContainmentSelect(opt.Some(p.containment));
					}
					// Set mode to display sidebar; fall back to View if user cannot edit this containment
					setMode(effectiveModeForContainment(p.containment, m));
				} else {
					// No row selected - select row and open sidebar
					openSidebar(p.containment, m);
				}
			}
		});
	};

	return (
		<>
			{/* Sidebar */}
			{selectedContainment.IsSome() && (
				<EditViewContainmentSidebar
					onClose={handleSidebarClose}
					sidebarProps={{
						pushContent: true, // Enable content pushing
					}}
					render={(m) => {
						//Retrieve the selected containment from the store, this updates when the containment is edited
						const selectedContainmentById = useSelector(
							(s: RootState) =>
								SelectContainmentById(
									s,
									selectedContainment.Expect(
										"Containment should exist"
									).id
								)
						);
						return m.Is("Read") ? (
							<ContainmentDetails
								key={fmt.Sprintf(
									"containment-%s",
									selectedContainment.Expect(
										"containment should exist"
									).id
								)}
								id={
									selectedContainment.Expect(
										"containment should exist"
									).id
								}
							/>
						) : (
							<EditContainment
								blockNavigation={showConfirmDialog}
								onEdit={filtersCtx.refresh}
								onClose={handleConfirmDiscard}
								key={fmt.Sprintf(
									"containment-%s",
									selectedContainment.Expect(
										"containment should exist"
									).id
								)}
								// Fall back to the selected containment if containment is not in table
								c={selectedContainmentById
									.IfSome((c) => c)
									.UnwrapOr(
										selectedContainment.Expect(
											"Containment should exist"
										)
									)}
								ref={editContainmentRef}
							/>
						);
					}}
					mode={mode}
				/>
			)}

			{/* Confirmation Dialog */}
			<MCCModal
				title="Discard Changes"
				message="Are you sure you want to discard unsaved changes to this containment?"
				onClose={() => setShowConfirmDialog(false)} // Just close dialog
				onConfirm={handleConfirmDiscard}
				open={showConfirmDialog}
			/>

			{/* Menu */}
			<Box sx={{ width: "100%" }}>
				<Menu
					id="basic-menu"
					anchorEl={menuProps.Map((o) => o?.el).UnwrapOrNil()}
					open={menuProps.IsSome()}
					onClose={handleClose}
					MenuListProps={{
						"aria-labelledby": "basic-button",
					}}
					sx={ContextMenuStyle()}
				>
					{containmentActions}
				</Menu>

				{/* Table */}
				<Box data-test-selector="dashboard-table">
					<BaseTable
						data={containments}
						loading={loading}
						order={filtersCtx.filters.sort}
						page={page}
						rowsPerPage={rowsPerPage}
						headCells={containmentHeadCells}
						config={{
							stickyHeader: true,
							header: {
								title: (
									<Typography
										variant="h5"
										sx={GradientHeaderText(
											theme,
											filtersActive
										)}
									>
										{title || "Containments"}
									</Typography>
								),
								actions: dashboardActions,
							},
						}}
						onRowClick={(c) => {
							// check for unsaved changes
							if (editContainmentRef.current?.hasChanges()) {
								// Store the information about which action to take after confirmation
								// For same row click, we want to close the sidebar
								// For different row click, we want to open a new sidebar
								const shouldClose = selectedContainment
									.IfSome((cont) => cont.id.Equal(c.id))
									.UnwrapOr(false);

								setPendingRowSelection(
									opt.Some({
										containment: c,
										action: shouldClose ? "close" : "open",
										mode: mode,
									})
								);

								// Show confirmation dialog
								setShowConfirmDialog(true);
							} else {
								// No unsaved changes, proceed normally
								if (
									selectedContainment
										.IfSome((cont) => cont.id.Equal(c.id))
										.UnwrapOr(false)
								) {
									closeSidebarAndResetState();
								} else {
									openSidebar(c, mode);
								}
							}
						}}
						getSelectedRow={(d) =>
							d.Find((c) =>
								selectedContainment
									.IfSome((cont) => cont.id.Equal(c.id))
									.UnwrapOr(false)
							)
						}
						onRowSelect={(c) =>
							// dont reselect if its the same id
							c
								.Filter((c) =>
									c.id.Equal(
										selectedContainment.Expect(
											"Containment not found"
										).id
									)
								)
								.IfSome((c) => onContainmentSelect(opt.Some(c)))
						}
						getRowId={(row) => row.id}
						renderActions={(row) => (
							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-end",
								}}
							>
								<Tooltip title="Actions">
									<IconButton
										data-test-selector="containment-row-actions"
										onClick={(e) => {
											e.stopPropagation(); // Prevent row click
											setMenuProps(
												opt.Some({
													el: e.currentTarget,
													containment: row,
												})
											);
										}}
									>
										<MenuIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>
						)}
						sx={TableHeaderStyles(theme)}
						{...tableProps}
					/>
				</Box>
			</Box>
		</>
	);
}
